<template>
    <!--
        Inline date picker with a preset sidebar, built on @vuepic/vue-datepicker.

        v-model is { preset, start, end }: start/end are ISO dates ("yyyy-MM-dd"),
        preset is the active preset's key (null when days were picked by hand and
        there is no manual preset). In single mode start always equals end.
        With enable-time it also carries startTime/endTime as 24-hour "HH:mm"
        wall-clock times in `timezone`; presets set whole days (00:00 - 23:59).

        Presets are plain objects (see utils/dateRangePresets.js), so each page
        chooses its own buttons; each preset names the pane it opens: the day
        calendar, a half-month chooser, or a month list. A blade, which cannot
        pass JS objects, can name built-in presets by key instead. Pass an empty
        presets array for a bare calendar.

        Usage, in a Vue component:
            <date-range-picker v-model="range" :timezone="company.timezone" disable-future-dates />
            <date-range-picker v-model="day" mode="single" />
            <date-range-picker v-model="range" enable-time time-format="12h" />
            <date-range-picker v-model="range" :presets="[TODAY, lastNDays(30), CUSTOM]" />

        Usage, in a blade <form>. `name` adds hidden inputs, so the form posts
        report[start], report[end] (and report[start_time], report[end_time],
        report[preset]); seed it with :model-value to restore old input:
            <date-range-picker name="report"
                               :presets='@json(["today", "last7Days", "last30Days", "month", "custom"])'
                               :timezone='@json($company->timezone)'
                               :model-value='@json(old("report"))'
                               disable-future-dates
            ></date-range-picker>

        To post flat field names that an existing controller already reads:
            <date-range-picker :input-names='@json(["start" => "start", "end" => "end"])'></date-range-picker>
    -->
    <div    class="date-range-picker rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div    class="border-b border-gray-300 pb-3 text-lg text-gray-500">
            Period: {{ periodLabel }}
        </div>

        <!-- Below `sm` the presets wrap in a row above the calendar, so the picker fits a phone. -->
        <div    class="mt-2 flex flex-col sm:flex-row">
            <div    v-if="presetList.length"
                    class="flex shrink-0 flex-row flex-wrap gap-2 border-b border-gray-200 pb-3 sm:w-32 sm:flex-col sm:flex-nowrap sm:border-b-0 sm:border-r sm:pb-0 sm:pr-3"
            >
                <button v-for="option in presetList"
                        :key="option.key"
                        type="button"
                        class="whitespace-nowrap rounded-md px-3 py-1.5 text-left text-sm"
                        :class="(option.key === presetKey) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
                        @click="selectPreset(option.key)"
                >{{ option.label }}</button>
            </div>

            <div    class="min-w-[15rem] flex-1"
                    :class="{ 'pt-3 sm:pl-4 sm:pt-0': presetList.length }"
            >
                <date-range-half-month-pane v-if="pane === 'halfMonth'"
                                            :start="start"
                                            :today="today"
                                            :max-date="maxDate"
                                            @select="onPaneSelect"
                ></date-range-half-month-pane>

                <date-range-month-pane  v-else-if="pane === 'month'"
                                        :start="start"
                                        :max-date="maxDate"
                                        @select="onPaneSelect"
                ></date-range-month-pane>

                <!-- Remounted on each preset click so it opens on the new range. -->
                <date-range-calendar-pane   v-else
                                            :key="'calendar-' + presetClicks"
                                            :mode="mode"
                                            :start="start"
                                            :end="end"
                                            :today="today"
                                            :max-date="maxDate"
                                            @select="onPaneSelect"
                ></date-range-calendar-pane>

                <date-range-time-fields v-if="enableTime"
                                        class="mt-4 border-t border-gray-200 pt-4"
                                        :mode="mode"
                                        :start-time="startTime"
                                        :end-time="endTime"
                                        :time-format="timeFormat"
                                        @update="onTimeUpdate"
                ></date-range-time-fields>
            </div>
        </div>

        <!-- Posts the selection with a plain <form>; see `name` / `inputNames`. -->
        <template v-if="fieldNames">
            <input  type="hidden"
                    :name="fieldNames.start"
                    :value="isoStart ?? ''"
            >
            <input  type="hidden"
                    :name="fieldNames.end"
                    :value="isoEnd ?? ''"
            >
            <template v-if="enableTime">
                <input  type="hidden"
                        :name="fieldNames.startTime"
                        :value="startTime"
                >
                <input  type="hidden"
                        :name="fieldNames.endTime"
                        :value="endTime"
                >
            </template>
            <input  type="hidden"
                    :name="fieldNames.preset"
                    :value="presetKey ?? ''"
            >
        </template>
    </div>
</template>

<script>
import { DateTime } from 'luxon'
import {
    DAY_END,
    DAY_START,
    DEFAULT_PRESETS,
    DEFAULT_SINGLE_PRESETS,
    clampRange,
    formatPeriod,
    formatPeriodWithTimes,
    fromIso,
    orderRange,
    orderTimes,
    paneOf,
    parseTime,
    resolvePreset,
    toIso,
    todayInZone,
} from '../../utils/dateRangePresets'
import DateRangeCalendarPane from './DateRangePicker/DateRangeCalendarPane.vue'
import DateRangeHalfMonthPane from './DateRangePicker/DateRangeHalfMonthPane.vue'
import DateRangeMonthPane from './DateRangePicker/DateRangeMonthPane.vue'
import DateRangeTimeFields from './DateRangePicker/DateRangeTimeFields.vue'

const PANES = ['calendar', 'halfMonth', 'month']

export default {
    name: 'DateRangePicker',
    components: { DateRangeCalendarPane, DateRangeHalfMonthPane, DateRangeMonthPane, DateRangeTimeFields },
    emits: ['update:modelValue'],
    props: {
        // { preset, start, end }; start/end are ISO dates. Null (or an invalid
        // value) selects the default preset.
        modelValue: {
            type: Object,
            default: null,
        },
        // 'range' picks a start and an end; 'single' picks one day.
        mode: {
            type: String,
            default: 'range',
            validator: value => ['range', 'single'].includes(value),
        },
        // Sidebar buttons, in order: preset objects, or built-in preset keys
        // ("today", "last30Days", ...). Null uses DEFAULT_PRESETS (range mode)
        // or DEFAULT_SINGLE_PRESETS (single mode); [] hides the sidebar.
        presets: {
            type: Array,
            default: null,
        },
        // Key of the preset selected when there is no value. Null uses the
        // first preset that has a range.
        defaultPreset: {
            type: String,
            default: null,
        },
        // IANA zone (e.g. the company's) that decides what "today" is for the
        // presets and the future-date cutoff. Null uses the browser's zone.
        timezone: {
            type: String,
            default: null,
        },
        // Reports have no data after today, but other uses may need future dates.
        // Limits dates only; times on today are not capped at the current time.
        disableFutureDates: {
            type: Boolean,
            default: false,
        },
        // Adds start/end time inputs and startTime/endTime to the v-model.
        enableTime: {
            type: Boolean,
            default: false,
        },
        // '24h' or '12h'. Only how times are shown and entered; the v-model
        // always holds 24-hour "HH:mm".
        timeFormat: {
            type: String,
            default: '24h',
            validator: value => ['24h', '12h'].includes(value),
        },
        // Adds hidden inputs so a plain <form> posts the selection as
        // name[start], name[end], name[start_time], name[end_time], name[preset].
        name: {
            type: String,
            default: null,
        },
        // Overrides single hidden input names, e.g. { start: 'start', end: 'end' }
        // for a controller that reads flat fields. Keys: start, end, startTime,
        // endTime, preset. Setting it also adds the hidden inputs.
        inputNames: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            presetKey: null,
            start: null,
            end: null,
            startTime: DAY_START,
            endTime: DAY_END,
            // Set when the user picks a period inside a preset's pane (e.g. a
            // past month), so a relative preset stops following today.
            pinned: false,
            presetClicks: 0,
            // Ticks once a minute so "today" rolls over at midnight.
            now: DateTime.now(),
            clock: null,
        }
    },
    created() {
        this.applyValue(this.modelValue)
    },
    mounted() {
        this.clock = setInterval(() => {
            this.now = DateTime.now()
        }, 60 * 1000)
    },
    beforeUnmount() {
        clearInterval(this.clock)
    },
    watch: {
        modelValue(value) {
            if (!this.differsFrom(value)) {
                return
            }
            this.applyValue(value)
        },
        // Midnight, or a new timezone.
        todayIso() {
            this.refreshForToday()
        },
        disableFutureDates() {
            this.enforceFutureLimit()
        },
    },
    computed: {
        presetList() {
            if (!this.presets) {
                return (this.mode === 'single') ? DEFAULT_SINGLE_PRESETS : DEFAULT_PRESETS
            }
            return this.presets.filter(entry => {
                if (resolvePreset(entry)) {
                    return true
                }
                console.warn(`DateRangePicker: unknown preset "${entry}" was skipped.`)
                return false
            }).map(resolvePreset)
        },
        // Hidden input names, or null when the picker does not post with a form.
        fieldNames() {
            if (!this.name && !this.inputNames) {
                return null
            }
            const field = key => (this.name ? `${this.name}[${key}]` : key)
            return {
                start: field('start'),
                end: field('end'),
                startTime: field('start_time'),
                endTime: field('end_time'),
                preset: field('preset'),
                ...this.inputNames,
            }
        },
        activePreset() {
            return this.presetList.find(preset => preset.key === this.presetKey) ?? null
        },
        // Preset that days picked by hand on the calendar belong to.
        manualPreset() {
            return this.presetList.find(preset => !preset.range && (paneOf(preset) === 'calendar')) ?? null
        },
        pane() {
            const pane = paneOf(this.activePreset)
            return PANES.includes(pane) ? pane : 'calendar'
        },
        today() {
            return todayInZone(this.timezone, this.now)
        },
        todayIso() {
            return toIso(this.today)
        },
        maxDate() {
            return this.disableFutureDates ? this.today : null
        },
        isoStart() {
            return this.start ? toIso(this.start) : null
        },
        isoEnd() {
            return this.end ? toIso(this.end) : null
        },
        periodLabel() {
            if (!this.start) {
                return (this.mode === 'single') ? 'Select a day' : 'Select a range'
            }
            if (this.enableTime) {
                return formatPeriodWithTimes(this.start, this.end, this.startTime, this.endTime, this.timeFormat)
            }
            return formatPeriod(this.start, this.end)
        },
    },
    methods: {
        // Take a value from the parent. Reversed or future dates are corrected
        // and emitted back; anything unreadable falls back to the default.
        applyValue(value) {
            const start = fromIso(value?.start)
            const end = value?.end ? fromIso(value.end) : start
            if (!start || !end) {
                this.selectDefault()
                return
            }
            // A blade posts '' for "no preset" (ConvertEmptyStringsToNull is off),
            // and a stale or hand-edited key may not be in this page's list.
            // Either way the dates were picked by hand.
            this.presetKey = this.presetList.some(preset => preset.key === value.preset)
                ? value.preset
                : (this.manualPreset?.key ?? null)
            // A restored value shows the dates it was saved with.
            this.pinned = true
            if (this.enableTime) {
                // Also read the snake_case names the hidden inputs post, so a
                // blade can seed the picker straight from old('report').
                const startTime = value.startTime ?? value.start_time
                const endTime = value.endTime ?? value.end_time
                this.startTime = parseTime(startTime) ? startTime : DAY_START
                this.endTime = parseTime(endTime) ? endTime : DAY_END
            }
            this.setRange({ start, end }, false)
            if (this.differsFrom(value)) {
                this.emitValue()
            }
        },
        // True when `value` is not what this picker would emit right now.
        differsFrom(value) {
            if ((value?.preset !== this.presetKey) || (value?.start !== this.isoStart) || (value?.end !== this.isoEnd)) {
                return true
            }
            return this.enableTime && ((value.startTime !== this.startTime) || (value.endTime !== this.endTime))
        },
        selectDefault() {
            const preset = this.presetList.find(option => option.key === this.defaultPreset)
                ?? this.presetList.find(option => option.range)
            if (preset) {
                this.selectPreset(preset.key)
                return
            }
            this.presetKey = this.manualPreset?.key ?? null
            this.start = null
            this.end = null
        },
        selectPreset(key) {
            const preset = this.presetList.find(option => option.key === key)
            if (!preset) {
                return
            }
            this.now = DateTime.now()
            this.presetKey = key
            this.pinned = false
            this.presetClicks++
            if (!preset.range) {
                // A manual preset keeps the current days until the user picks new ones.
                this.emitValue()
                return
            }
            this.startTime = DAY_START
            this.endTime = DAY_END
            this.setRange(preset.range(this.today))
        },
        onPaneSelect(range) {
            if (this.pane === 'calendar') {
                // Days picked by hand belong to the manual preset, whichever preset was active.
                this.presetKey = (this.activePreset && !this.activePreset.range)
                    ? this.activePreset.key
                    : (this.manualPreset?.key ?? null)
            } else {
                this.pinned = true
            }
            this.setRange(range)
        },
        refreshForToday() {
            const preset = this.activePreset
            if (preset?.relative && preset.range && !this.pinned) {
                this.setRange(preset.range(this.today))
                return
            }
            this.enforceFutureLimit()
        },
        enforceFutureLimit() {
            if (!this.disableFutureDates || !this.end || (this.end <= this.today)) {
                return
            }
            this.setRange({ start: this.start, end: this.end })
        },
        setRange(range, emit = true) {
            let next = orderRange(range)
            if (this.mode === 'single') {
                next = { start: next.start, end: next.start }
            }
            if (this.disableFutureDates) {
                next = clampRange(next, this.today)
            }
            this.start = next.start
            this.end = next.end
            this.normalizeTimes()
            if (emit) {
                this.emitValue()
            }
        },
        onTimeUpdate(times) {
            this.startTime = times.startTime
            this.endTime = times.endTime
            this.normalizeTimes()
            this.emitValue()
        },
        // Single mode is one moment; otherwise swap reversed times on a one-day range.
        normalizeTimes() {
            if (this.mode === 'single') {
                this.endTime = this.startTime
                return
            }
            const times = orderTimes(this.start, this.end, this.startTime, this.endTime)
            this.startTime = times.startTime
            this.endTime = times.endTime
        },
        emitValue() {
            if (!this.start) {
                return
            }
            const value = { preset: this.presetKey, start: this.isoStart, end: this.isoEnd }
            if (this.enableTime) {
                value.startTime = this.startTime
                value.endTime = this.endTime
            }
            this.$emit('update:modelValue', value)
        },
    },
}
</script>

<style scoped>
/* Theme shared by every pane. */
.date-range-picker :deep(.dp__theme_light) {
    --dp-primary-color: #2563eb;
    --dp-range-between-dates-background-color: #dbeefa;
    --dp-range-between-dates-text-color: #111827;
    --dp-range-between-border-color: transparent;
    --dp-cell-size: 32px;
    --dp-cell-border-radius: 6px;
    --dp-font-size: 1rem;
    --dp-menu-border-color: transparent;
    --dp-border-color: transparent;
}

.date-range-picker :deep(.dp__menu) {
    border: none;
}
</style>
